import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { LoadingSpinner } from '../../components/ui/loading-spinner';
import supabase from '../../supabase';
import { getErrorMessage } from '../../helpers/get-error-message';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isEmailSent, setIsEmailSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) throw error;
      setIsEmailSent(true);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to send reset email'));
    } finally {
      setIsLoading(false);
    }
  };

  if (isEmailSent) {
    return (
      <div className="flex min-h-[100dvh] items-center justify-center bg-background">
        <div className="mx-auto flex w-full flex-col justify-center space-y-6 sm:w-[350px] px-8">
          <div className="flex flex-col space-y-2 text-center">
            <h1 className="text-2xl font-semibold tracking-tight">
              Check your email
            </h1>
            <p className="text-sm text-muted-foreground">
              We've sent you a password reset link. Please check your email and
              follow the instructions to reset your password.
            </p>
          </div>
          <div className="text-center">
            <Link
              to="/sign-in"
              className="text-accent-purple hover:text-accent-purple/80"
            >
              Back to Sign In
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-[100dvh] items-center justify-center bg-bg-main">
      <div className="mx-auto flex w-full flex-col justify-center space-y-6 sm:w-[350px] px-8">
        <div className="flex flex-col space-y-2 text-center">
          <h1 className="text-2xl font-semibold tracking-tight">
            Forgot Password
          </h1>
          <p className="text-sm text-muted-foreground">
            Enter your email address and we'll send you a link to reset your
            password.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Input
              id="email"
              placeholder="name@example.com"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
              disabled={isLoading}
              className="h-12 bg-white border-outline focus:border-accent-purple focus:ring-accent-purple focus:outline-none"
              style={{ fontSize: '16px' }}
            />
            {error && (
              <p className="text-sm text-red-500 text-center">{error}</p>
            )}
          </div>
          <Button
            type="submit"
            className="w-full h-12 bg-accent-purple hover:bg-accent-purple/90 text-white rounded-full py-6 flex items-center justify-center"
            disabled={isLoading || !email}
          >
            {isLoading ? <LoadingSpinner /> : 'Send Reset Link'}
          </Button>
        </form>

        <div className="text-center">
          <Link
            to="/sign-in"
            className="text-accent-purple hover:text-accent-purple/80"
          >
            Back to Sign In
          </Link>
        </div>
      </div>
    </div>
  );
}
